import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FiPlus, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import API from '../../utils/api';
import './ProductPicker.css';

/* ────────────────────────────────────────────────────────────────────
   ProductPicker
   Replaces the plain "Product name" textarea in a sales-order line.
   • Free typing still works (line stays a plain text line).
   • Dropdown lists draft + active catalog products (name / SKU / HSN / GST %).
   • "Add … as new draft product" opens a small modal (only name is required).
   ──────────────────────────────────────────────────────────────────── */

// Categories rarely change — cache for the session
let categoryCache = null;

const gstLabel = (g) => (g === null || g === undefined ? '' : `GST ${g}%`);

/* ── Quick-add modal ─────────────────────────────────────────────────── */
const QuickAddModal = ({ initialName, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: initialName || '', categoryText: '', sku: '', hsnCode: '', gstPercent: '' });
  const [categories, setCategories] = useState(categoryCache || []);
  const [saving, setSaving] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => { nameRef.current?.focus(); nameRef.current?.select(); }, []);

  useEffect(() => {
    if (categoryCache) return;
    API.get('/catalog/categories')
      .then(({ data }) => {
        categoryCache = (data.categories || []).filter((c) => c.status !== 'archived');
        setCategories(categoryCache);
      })
      .catch(() => { /* dropdown just falls back to "Uncategorized" */ });
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // typed text matches an existing category (case-insensitive)? → use it, else it is a NEW category
  const catText = form.categoryText.trim();
  const matchedCategory = catText
    ? categories.find((c) => c.name.trim().toLowerCase() === catText.toLowerCase())
    : null;
  const isNewCategory = !!catText && !matchedCategory;

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Product name is required'); return; }
    setSaving(true);
    try {
      const { data } = await API.post('/sales-order-products/quick-add', {
        name: form.name.trim(),
        categoryId: matchedCategory ? matchedCategory.id : null,
        categoryName: isNewCategory ? catText : '',
        sku: form.sku.trim(),
        hsnCode: form.hsnCode.trim(),
        gstPercent: form.gstPercent,
      });
      if (data.categoryCreated && data.category) {
        categoryCache = [...(categoryCache || categories), data.category];
        setCategories(categoryCache);
      }
      toast.success(data.categoryCreated
        ? `Added as draft in new category “${data.category.name}”`
        : 'Added to catalog as draft');
      onCreated(data.product);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not add product');
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    if (e.key === 'Enter' && e.target.tagName !== 'SELECT') { e.preventDefault(); submit(); }
  };

  return ReactDOM.createPortal(
    <div
      className="pp-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onBlur={(e) => e.stopPropagation()}
      onFocus={(e) => e.stopPropagation()}
    >
      <div className="pp-modal" onKeyDown={onKeyDown}>
        <div className="pp-modal-head">
          <h3>Add new product <span className="pp-badge pp-badge--draft">Draft</span></h3>
          <button type="button" className="pp-x" onClick={onClose}><FiX size={16} /></button>
        </div>

        <div className="pp-modal-body">
          <label className="pp-field pp-field--full">
            <span>Product name *</span>
            <input ref={nameRef} value={form.name} onChange={set('name')} maxLength={200} />
          </label>

          <label className="pp-field pp-field--full">
            <span>Category <em>(pick one or type a new name)</em></span>
            <input
              list="pp-category-list"
              value={form.categoryText}
              onChange={set('categoryText')}
              placeholder="Uncategorized (default)"
              maxLength={200}
              autoComplete="off"
            />
            <datalist id="pp-category-list">
              {categories.map((c) => <option key={c.id} value={c.name} />)}
            </datalist>
            {isNewCategory && (
              <small className="pp-newcat">New category “{catText}” will be created</small>
            )}
          </label>

          <label className="pp-field">
            <span>SKU <em>(optional)</em></span>
            <input value={form.sku} onChange={set('sku')} placeholder="Auto-generated if blank" maxLength={100} />
          </label>

          <label className="pp-field">
            <span>HSN code <em>(optional)</em></span>
            <input value={form.hsnCode} onChange={set('hsnCode')} placeholder="4, 6 or 8 digits" maxLength={8} inputMode="numeric" />
          </label>

          <label className="pp-field">
            <span>GST % <em>(optional)</em></span>
            <input type="number" min="0" max="100" step="any" value={form.gstPercent} onChange={set('gstPercent')} />
          </label>
        </div>

        <div className="pp-modal-foot">
          <span className="pp-hint">Saved to the catalog as a draft — complete it later from Products.</span>
          <button type="button" className="so-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="so-btn so-btn--primary" onClick={submit} disabled={saving}>
            {saving ? 'Adding…' : 'Add & use'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ── Picker (textarea + dropdown) ────────────────────────────────────── */
const ProductPicker = ({ value, onTextChange, onPick, inputRef, placeholder = 'Product name' }) => {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const [modalOpen, setModalOpen] = useState(false);
  const [pos, setPos] = useState(null);

  const taRef = useRef(null);
  const listRef = useRef(null);
  const reqRef = useRef(0);

  const q = (value || '').trim();
  const showAdd = q.length > 0 && !results.some((r) => r.name.toLowerCase() === q.toLowerCase());
  const total = results.length + (showAdd ? 1 : 0);

  /* search (debounced, stale-response safe) */
  useEffect(() => {
    if (!open || !q) return undefined;     // nothing typed → no search, no list
    const reqId = ++reqRef.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await API.get('/sales-order-products/search', { params: { q, limit: 12 } });
        if (reqId === reqRef.current) { setResults(data.products || []); setActive(-1); }
      } catch {
        if (reqId === reqRef.current) setResults([]);
      } finally {
        if (reqId === reqRef.current) setLoading(false);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [q, open]);

  useEffect(() => { if (!open) setResults([]); }, [open]);

  /* position the fixed dropdown under (or above) the textarea */
  const place = useCallback(() => {
    const el = taRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const width = Math.max(r.width, 340);
    const left = Math.min(r.left, window.innerWidth - width - 8);
    const maxH = 280;
    if (below < 220 && r.top > below) {
      setPos({ left: Math.max(8, left), width, bottom: window.innerHeight - r.top + 2, maxH: Math.min(maxH, r.top - 12) });
    } else {
      setPos({ left: Math.max(8, left), width, top: r.bottom + 2, maxH: Math.min(maxH, below - 12) });
    }
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place, results.length]);

  /* keep keyboard-highlighted row visible */
  useEffect(() => {
    if (active < 0) return;
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const setRefs = (el) => {
    taRef.current = el;
    if (typeof inputRef === 'function') inputRef(el);
  };

  const choose = (idx) => {
    if (idx < results.length) {
      onPick(results[idx]);
      setOpen(false);
    } else {
      setOpen(false);
      setModalOpen(true);
    }
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown' && total) { e.preventDefault(); setActive((a) => (a + 1) % total); }
    else if (e.key === 'ArrowUp' && total) { e.preventDefault(); setActive((a) => (a <= 0 ? total - 1 : a - 1)); }
    else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); choose(active); }   // Enter w/o highlight = normal newline
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
  };

  return (
    <>
      <textarea
        ref={setRefs}
        className="so-line-product"
        placeholder={placeholder}
        value={value}
        rows={1}
        autoComplete="off"
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        onChange={(e) => {
          onTextChange(e.target.value);
          setOpen(e.target.value.trim().length > 0);   // list appears only once typing starts
          e.target.style.height = 'auto';
          e.target.style.height = `${e.target.scrollHeight}px`;
        }}
      />

      {open && q && pos && ReactDOM.createPortal(
        <div
          ref={listRef}
          className="pp-list"
          style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxH }}
          // keep focus in the textarea so the row's blur handler doesn't fire mid-click
          onMouseDown={(e) => e.preventDefault()}
          onBlur={(e) => e.stopPropagation()}
          onFocus={(e) => e.stopPropagation()}
        >
          {loading && results.length === 0 && <div className="pp-empty">Searching…</div>}
          {!loading && results.length === 0 && !showAdd && <div className="pp-empty">No draft or active products yet</div>}

          {results.map((p, i) => (
            <div
              key={p.id}
              data-idx={i}
              className={`pp-item ${active === i ? 'is-active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(i)}
            >
              <div className="pp-item-top">
                <span className="pp-item-name">{p.name}</span>
                <span className={`pp-badge pp-badge--${p.status}`}>{p.status === 'draft' ? 'Draft' : 'Active'}</span>
              </div>
              <div className="pp-item-meta">
                {[p.sku && `SKU ${p.sku}`, p.hsnCode && `HSN ${p.hsnCode}`, gstLabel(p.gstPercent)].filter(Boolean).join('  ·  ') || 'No SKU / HSN / GST set'}
              </div>
            </div>
          ))}

          {showAdd && (
            <div
              data-idx={results.length}
              className={`pp-item pp-item--add ${active === results.length ? 'is-active' : ''}`}
              onMouseEnter={() => setActive(results.length)}
              onClick={() => choose(results.length)}
            >
              <FiPlus size={14} /> <span>Add “{q}” as new draft product</span>
            </div>
          )}
        </div>,
        document.body
      )}

      {modalOpen && (
        <QuickAddModal
          initialName={q}
          onClose={() => { setModalOpen(false); taRef.current?.focus(); }}
          onCreated={(product) => { setModalOpen(false); onPick(product); }}
        />
      )}
    </>
  );
};

export default ProductPicker;